/**
 * WebGL Context Loss Recovery
 *
 * Handles browser-initiated WebGL context loss (e.g. GPU memory pressure,
 * device sleep, tab backgrounding) and auto-recovery.
 *
 * Usage:
 *   const unregister = registerContextLossHandler(renderer)
 *   // later:
 *   unregister()
 */

/**
 * Prevent the browser from giving up on a lost WebGL context,
 * and force Three.js to reset its internal state once restored.
 *
 * @param {WebGLRenderer} renderer — the Three.js renderer whose canvas we guard
 * @returns {Function} call to remove listeners
 */
export const registerContextLossHandler = (renderer) => {
    const canvas = renderer?.domElement
    if (!canvas) return () => {}

    const handleLost = (event) => {
        event.preventDefault()
    }

    const handleRestored = () => {
        renderer.resetState()
    }

    canvas.addEventListener('webglcontextlost', handleLost)
    canvas.addEventListener('webglcontextrestored', handleRestored)

    return () => {
        canvas.removeEventListener('webglcontextlost', handleLost)
        canvas.removeEventListener('webglcontextrestored', handleRestored)
    }
}
